/**
 * Workflow Studio contracts — `/agents/workflows/[slug]` (RO-3-02b, PRD §11.3).
 *
 * ── Why this file exists ──
 *
 * The Next page was an async Server Component that read the vault directly
 * through eleven `@/optimization/*` loaders and then handed FIVE server-action
 * function references down through props (`runAction={runLoopNowAction}`, and
 * four more imported straight into the client islands). Neither half survives a
 * Vite client: there is no server render to read the vault from, and a function
 * cannot cross the wire. So the page's data assembly becomes ONE read
 * (`detail`), and each server action becomes one named mutation below.
 *
 * The mutation set is deliberately a 1:1 map of the actions in
 * `excellent/src/optimization/{actions,trigger-actions}.ts` — same names, same
 * arguments, same return shapes — so the Hono handler is that action's body
 * verbatim minus `revalidatePath`. A port, not a redesign.
 *
 *   - `activateWorkflowAction(slug)` is `setLoopStatusAction(slug, "active")`;
 *     the client calls `setStatus` with `"active"` rather than paying for a
 *     second endpoint that only curries an argument.
 *   - `resumeLoopAction` IS here (RO-2-09e / #44418). Call sites live on
 *     `page.tsx` / `_runs-live.tsx` / `_studio.tsx` (prop-drilled); an earlier
 *     note that claimed "no call site" was wrong.
 *   - Verdict-queue + optimization-settings actions live in sibling contracts
 *     (`verdicts.ts`, `optimization-settings.ts`) — same source files, different
 *     surfaces.
 *
 * ── What is NOT here, on purpose ──
 *
 * No edges table, no predicate DSL, no second recursion mechanism.
 * `docs/agents/RECURSIVE-WORKFLOWS-COMPLETION-PRD.md` §7 lists a second
 * predicate DSL as an explicit non-goal, and branching/recursion already work
 * and are mutation-tested there. This contract carries the loop's steps and
 * telemetry exactly as the engine already models them; the peer program's
 * panels attach to `WorkflowDetailPayload`, they do not re-derive it.
 */

import { z } from "zod";
import type { Args, Operation } from "../../types";
import { scoped } from "../../types";
// Value imports (not `import type`): `invalidates` names the target Operation
// OBJECT, so these have to survive erasure. Both are pure contract modules —
// zod schemas + plain objects, no cycle back to this file.
import { workflowFleet } from "./fleet";
import { loopsRunnability } from "./runnability";
import { agentRuns } from "../agent-runs";
import type { LadderStep, LadderStepFailures } from "../../../blocks/optimization/ladder-view";
import type { ParetoPoint } from "../../../blocks/optimization/pareto-chart";
// Type-only, and from the zero-dependency subpath rather than the barrel — the
// barrel's chain reaches `security/{keystore,keychain}` and this contract is
// imported by client code (`import type` is erased, but the subpath keeps the
// habit correct and survives someone later needing the value).
import type { EventTypeGroup } from "@excellent/core/trigger-builder";

// ── Range vocabulary ─────────────────────────────────────────────────────────
//
// Mirror of `DETAIL_RANGES` in `excellent/src/optimization/workflow-detail-view.ts`,
// which stays the source of truth for the LABELS and the day counts (the client
// imports it from there — it is a pure module by construction, see its header).
// Only the wire-validation enum is duplicated, because `packages/ui` may not
// import from `excellent/src`.

export const workflowDetailRange = z.enum(["1d", "7d", "30d", "all"]);
export type WorkflowDetailRange = z.infer<typeof workflowDetailRange>;

// ── The read payload ─────────────────────────────────────────────────────────

/** One step of the pipeline, with its lifetime telemetry (`loopStepTelemetry`). */
export interface WorkflowStepDto {
  id: number;
  ordinal: number;
  name: string;
  rung: number;
  role: string | null;
  model: string | null;
  runs: number;
  avg_cost: number;
  avg_tokens: number;
  avg_wall_ms: number;
  /**
   * `LoopStepFailures` in `optimization/db.ts` is structurally identical to the
   * ladder block's own failure shape, so the wire carries ONE of them rather
   * than two names for the same four fields.
   */
  failures: LadderStepFailures;
  /**
   * `excellent_loop_steps.config`, verbatim JSON — the step's authored control
   * flow, including `{"onError":{"step":"heal"}}`.
   *
   * This is the ONE widening this contract makes beyond what the Next page
   * rendered, and it is the widening the rollover OWES the peer program
   * (REACT-ROLLOVER-PRD §11.2b: "widen the contracts, once, for both programs").
   *
   * §11.1's finding is that the engine has honored `onError` for a long time and
   * is mutation-tested, but NOTHING DRAWS IT: `planTopology` emits a typed
   * `TopologyEdge{kind:"on_error"}` with zero UI call sites, and the Pipeline tab
   * is a flat `steps.map` rail. The renderer is the peer program's W2-A2 — a UI
   * surface, explicitly NOT the rollover's (§11.2a). Carrying the data here means
   * W2-A2 lands as a renderer change with no endpoint work behind it, which is
   * the whole point of porting `/agents` first.
   *
   * Raw JSON, deliberately unparsed: the engine owns that shape and a second
   * parser is a second place for it to drift.
   */
  config: string;
}

/**
 * The Overview tab's value + health window (`loopOverviewWindow`).
 *
 * `valueView` is the SAME projection the server page passed down — the windowed
 * measured layer, its projection, the annual line, and the assumptions those
 * two were computed from. The assumptions travel because the Value card prints
 * them ("N completed runs × M min of human work at $X/hr"): a derived dollar
 * figure without its inputs on screen is the number nobody can check.
 * Shapes mirror `MeasuredLayer` / `ProjectedLayer` / `ValueAssumptions` in
 * `excellent/src/optimization/value-model.ts` exactly.
 */
export interface WorkflowOverviewDto {
  range: WorkflowDetailRange;
  days: number | null;
  valueView: {
    measured: {
      runs: number;
      completed_runs: number;
      issues_caught: number;
      agent_cost_usd: number;
      realized_savings_usd: number;
    };
    /**
     * The six fields `OverviewWindow.valueView.projected` declares. A full
     * `ProjectedLayer` also carries `realized_savings_usd`, but that projection
     * type deliberately narrows it away (the measured layer already carries it,
     * and the Value card reads it from there) — so the wire mirrors the DECLARED
     * shape rather than widening it to what happens to be on the object.
     */
    projected: {
      time_saved_hours: number;
      time_saved_usd: number;
      issues_avoided_usd: number;
      gross_value_usd: number;
      net_value_usd: number;
      roi: number | null;
    };
    annual: { runs_per_day: number; net_value_usd: number };
    assumptions: {
      labor_rate_usd_per_hour: number;
      minutes_per_run: number;
      cost_per_caught_issue: number;
    };
  };
  runHealth: {
    total: number;
    completed: number;
    runningNow: number;
    passRate: number;
    avg_cost_usd: number;
    avg_wall_ms: number;
    byRole: Array<{
      role: string;
      runs: number;
      avg_cost: number;
      avg_tokens: number;
      avg_wall_ms: number;
    }>;
  };
  daily: Array<{ day: string; runs: number; completed: number; running: number; cost: number }>;
}

/** The trigger card's live state (`readTriggerCardState` + the event catalog). */
export interface WorkflowTriggerDto {
  eventGroups: EventTypeGroup[];
  subscription: {
    uid: string;
    name: string;
    event_type_prefix: string;
    aggregate_kind: string | null;
    enabled: boolean;
    debounce_seconds: number;
    max_fires_per_hour: number;
    last_fired_at: string | null;
  } | null;
  autorunEnabled: boolean;
  drainerLive: boolean;
}

/** Everything the Optimization tab renders. */
export interface WorkflowOptimizeDto {
  objective: string;
  gatePolicy: string;
  /** The step role the prompt diff is taken against (`primaryRole(steps)`). */
  role: string;
  variants: Array<{
    id: number;
    version: number;
    state: string;
    created_by: string;
    rationale: string | null;
    eval_summary: string | null;
    created_at: string;
  }>;
  promptDiff: {
    incumbentPrompt: string;
    candidatePrompt: string;
    scoreboard: { incumbent_tokens: number; candidate_tokens: number; delta_pct: number };
  } | null;
  driftVariantIds: number[];
  promotions: Array<{
    id: number;
    step_name: string;
    from_rung: number;
    to_rung: number;
    artifact_path: string | null;
    artifact_hash: string | null;
    state: string;
    drift: number | null;
  }>;
  evalCases: Array<{
    id: number;
    input: string;
    reference: string | null;
    source: string;
    labels: string | null;
  }>;
  rubric: { judge_model: string; agreement: number | null; updated_at: string } | null;
  paretoPoints: ParetoPoint[];
  pendingObjective: { objective: string; proposed_by: string; proposed_at: string } | null;
}

export interface WorkflowDetailPayload {
  slug: string;
  loop: {
    id: number;
    title: string;
    description: string | null;
    domain: string | null;
    gate_policy: string;
    status: string;
    owner_agent_id: string;
    objective: string;
  };
  activeDays: number;
  activeVersion: number | null;
  steps: WorkflowStepDto[];
  totalFailed: number;
  overview: WorkflowOverviewDto;
  trigger: WorkflowTriggerDto;
  /** Steps whose role has no authored definition — blocks the run control. */
  missingRoles: string[];
  /** Placeholder for the run console's env input, from step 1's `io.input_env`. */
  initialEnvHint: string;
  ladderSteps: LadderStep[];
  availableRoles: string[];
  /** Step names that are THEMSELVES workflows, so only real doorways are drawn. */
  stepWorkflowSlugs: string[];
  optimize: WorkflowOptimizeDto;
}

const detailParams = z.object({ slug: z.string().min(1) });
const detailQuery = z.object({ range: workflowDetailRange.optional() });

/**
 * READ — the whole studio payload for one slug + range.
 *
 * One endpoint rather than four (one per tab) because the ORIGINAL was one
 * server render: the header's meta strip, the Overview hero, the Pipeline rail
 * and the Optimization tab all read from the same `loadWorkflowDetail` call and
 * must agree. Splitting it would let two tabs disagree about the same number,
 * which is the exact class of defect the parity audit's §4 F12 was
 * (`1,104 of 1,935 · 54% pass`, a rate its own numerators compute to 57%).
 * Returns `null` when the slug is unknown — the route renders not-found from
 * that rather than the transport inventing a 404.
 */
const detailOp: Operation<
  { slug: string },
  z.infer<typeof detailQuery>,
  void,
  { detail: WorkflowDetailPayload | null }
> = {
  method: "GET",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/detail`,
  params: detailParams,
  query: detailQuery,
  response: z.custom<{ detail: WorkflowDetailPayload | null }>(),
};

// ── Mutations ────────────────────────────────────────────────────────────────
//
// Each returns the SAME shape its server action returned, so the ported client
// island's `if (res.ok) … else res.error` branches are unchanged.
//
// ── Declared invalidation (RO-5-02, PRD §13 R2) ──
//
// Every `invalidates` below is the port of a `revalidatePath` call that
// `excellent/server/routes/agent-workflows.ts` DELETED when it converted these
// actions — that file's header lists the originals per action, which is the
// inventory this pass consumes. The two revalidated Next paths map 1:1 onto the
// two reads that now serve them:
//
//   revalidatePath("/agents/workflows/<slug>")  →  `detailOp`, scoped to slug
//   revalidatePath("/agents/workflows")         →  `workflowFleet.list` (bare:
//                                                  every `range` variant)
//   revalidatePath("/agents")           (run only) →  `agentRuns.feed`
//
// A target is listed only when the read's OWN payload actually changes. That is
// a per-field claim, not a blanket one, so the two directions are stated where
// they are not obvious:
//
//  - `workflowFleet.list` is named only by mutations that move a field of
//    `FleetRow` (`status`, `active_version`, `runs`/`cost`/`tokens`/
//    `last_run_at`, `trigger_enabled`). Objective edits, step-model routing and
//    the trigger's own config are NOT in that row, so they do not name it.
//  - `loopsRunnability.get` counts ALL loops and blocks purely on
//    `excellent_loop_steps.role` (`loopMissingRoleMap`). So `setStepRole` moves
//    it and `setStatus` does NOT — status is not part of that query. This one is
//    an addition to the original mapping rather than a port of it: the KPI strip
//    is a client block that fetches its own endpoint, so the Next
//    `revalidatePath("/agents/workflows")` could never have refreshed it. It is
//    named here because the read genuinely goes stale; flagged in the RO-5-02
//    evidence as a deliberate correction, not an accident.
// Every mutation in this file is `{ slug }`-scoped and narrows `detailOp` the
// same way, so the mapper is declared once instead of fifteen times.
// `scoped()` type-checks the return against detailOp's TParams (#44311).
const detailTarget = scoped(
  detailOp,
  (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }),
);

/** The `{ ok, data?, error? }` envelope every `optimize`-CLI action returns. */
export type OptimizeCliResult = { ok: boolean; data?: unknown; error?: string };
const optimizeCliResult = z.custom<OptimizeCliResult>();

const setStatusBody = z.object({ status: z.enum(["active", "paused", "draft"]) });
const setStatusOp: Operation<
  { slug: string },
  void,
  z.infer<typeof setStatusBody>,
  { ok: boolean; status?: string; error?: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/status`,
  params: detailParams,
  body: setStatusBody,
  response: z.custom<{ ok: boolean; status?: string; error?: string }>(),
  // `setLoopStatusAction` → `/agents/workflows` + `/agents/workflows/<slug>`.
  // `FleetRow.status` is both a rendered column and the fleet query's ORDER BY
  // key, so a status change reorders the table, not just one cell.
  invalidates: [detailTarget, workflowFleet.list],
};

const runBody = z.object({
  /** `KEY=value` seed env for step 1. Keys are re-validated server-side. */
  env: z.record(z.string()).optional(),
});
const runOp: Operation<
  { slug: string },
  void,
  z.infer<typeof runBody>,
  { ok: true } | { ok: false; reason: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/run`,
  params: detailParams,
  body: runBody,
  response: z.custom<{ ok: true } | { ok: false; reason: string }>(),
  // `runLoopNowAction` → `/agents/workflows/<slug>` + `/agents`. Spawning a run
  // writes an `excellent_worker_runs` row, which is what BOTH the fleet row's
  // windowed `runs`/`cost`/`tokens`/`last_run_at` and the `/agents` worker-run
  // feed are computed from. `agentRuns.feed` is a keyset feed, so the bare
  // reference is required: it wildcards every `role`/`goal_id` filter and every
  // cursor page, and an exact-key `mutate` would have missed all of them.
  invalidates: [detailTarget, workflowFleet.list, agentRuns.feed],
};

const resumeBody = z.object({
  runId: z.number().int().positive(),
  env: z.record(z.string()).optional(),
});
export type ResumeBody = z.infer<typeof resumeBody>;
const resumeOp: Operation<
  { slug: string },
  void,
  ResumeBody,
  { ok: true } | { ok: false; reason: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/resume`,
  params: detailParams,
  body: resumeBody,
  response: z.custom<{ ok: true } | { ok: false; reason: string }>(),
  // `resumeLoopAction` → same revalidate surface as run (`/agents/workflows/<slug>`
  // + `/agents`). Spawns `run-loop --resume <runId>`; the new step runs write the
  // same worker_run rows the fleet + feed read.
  invalidates: [detailTarget, workflowFleet.list, agentRuns.feed],
};

const optimizeNowBody = z.object({ role: z.string().optional() });
const optimizeNowOp: Operation<
  { slug: string },
  void,
  z.infer<typeof optimizeNowBody>,
  OptimizeCliResult
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/optimize`,
  params: detailParams,
  body: optimizeNowBody,
  response: optimizeCliResult,
  // `optimizeNowAction` → `/agents/workflows/<slug>` only. It PROPOSES a variant
  // (the studio's Optimization tab) without promoting one, so no `FleetRow`
  // field — `active_version` included — moves.
  invalidates: [detailTarget],
};

const routeStepBody = z.object({
  step: z.string().min(1),
  model: z.string().min(1),
  escalateBelow: z.number().optional(),
});
const routeStepOp: Operation<
  { slug: string },
  void,
  z.infer<typeof routeStepBody>,
  OptimizeCliResult
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/route-step`,
  params: detailParams,
  body: routeStepBody,
  response: optimizeCliResult,
  // `routeStepAction` → `/agents/workflows/<slug>` only. This writes the step's
  // MODEL + escalation threshold (`optimize route`), never its `role` — so
  // unlike `setStepRole` below it cannot change `loopsRunnability`, whose
  // blocking set is keyed on `excellent_loop_steps.role` alone.
  invalidates: [detailTarget],
};

const setStepRoleBody = z.object({ step: z.string().min(1), role: z.string().min(1) });
const setStepRoleOp: Operation<
  { slug: string },
  void,
  z.infer<typeof setStepRoleBody>,
  { ok: true } | { ok: false; error: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/step-role`,
  params: detailParams,
  body: setStepRoleBody,
  response: z.custom<{ ok: true } | { ok: false; error: string }>(),
  // `updateStepRoleAction` → `/agents/workflows/<slug>`. Plus the runnability KPI:
  // `loopMissingRoleMap()` reads `excellent_loop_steps.role` and compares it to
  // `knownRoleSlugs()`, so re-roling a step is exactly the write that can flip a
  // loop between `runnable` and `blocked` and change `blocked_roles`.
  invalidates: [detailTarget, loopsRunnability.get],
};

const setObjectiveBody = z.object({ objective: z.string() });
const setObjectiveOp: Operation<
  { slug: string },
  void,
  z.infer<typeof setObjectiveBody>,
  { ok: boolean; pending?: boolean; error?: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/objective`,
  params: detailParams,
  body: setObjectiveBody,
  response: z.custom<{ ok: boolean; pending?: boolean; error?: string }>(),
  // The three objective actions each revalidated `/agents/workflows/<slug>` and
  // nothing else. The objective is not a `FleetRow` column, so the fleet console
  // is genuinely unaffected — naming it here would be over-invalidation.
  invalidates: [detailTarget],
};

const objectiveVerdict = z.custom<{ ok: boolean; error?: string }>();
const approveObjectiveOp: Operation<{ slug: string }, void, void, { ok: boolean; error?: string }> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/objective/approve`,
  params: detailParams,
  response: objectiveVerdict,
  invalidates: [detailTarget],
};
const discardObjectiveOp: Operation<{ slug: string }, void, void, { ok: boolean; error?: string }> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/objective/discard`,
  params: detailParams,
  response: objectiveVerdict,
  invalidates: [detailTarget],
};

const promoteVariantBody = z.object({
  variantId: z.number().int().positive(),
  toState: z.enum(["shadow", "canary", "active"]).optional(),
});
const promoteVariantOp: Operation<
  { slug: string },
  void,
  z.infer<typeof promoteVariantBody>,
  OptimizeCliResult
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/variants/promote`,
  params: detailParams,
  body: promoteVariantBody,
  response: optimizeCliResult,
  // Promote/rollback both move which variant is live, and `FleetRow` renders
  // `active_version` — so unlike `optimizeNow` above, these two do reach the
  // fleet console.
  invalidates: [detailTarget, workflowFleet.list],
};

const rollbackOp: Operation<{ slug: string }, void, void, OptimizeCliResult> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/rollback`,
  params: detailParams,
  response: optimizeCliResult,
  invalidates: [detailTarget, workflowFleet.list],
};

const promotionBody = z.object({ promotionId: z.number().int().positive() });
const approvePromotionOp: Operation<
  { slug: string },
  void,
  z.infer<typeof promotionBody>,
  OptimizeCliResult
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/promotions/approve`,
  params: detailParams,
  body: promotionBody,
  response: optimizeCliResult,
  // Approving a pending promotion IS the promotion — `active_version` moves, so
  // the fleet row does too. Rejecting one only clears the pending record, which
  // lives on the detail payload alone; that asymmetry is why these two adjacent
  // routes carry different declarations.
  invalidates: [detailTarget, workflowFleet.list],
};
const rejectPromotionOp: Operation<
  { slug: string },
  void,
  z.infer<typeof promotionBody>,
  OptimizeCliResult
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/promotions/reject`,
  params: detailParams,
  body: promotionBody,
  response: optimizeCliResult,
  invalidates: [detailTarget],
};

// ── Trigger mutations (Data Design PRD §6.7) ─────────────────────────────────

const saveTriggerBody = z.object({
  name: z.string(),
  event_type_prefix: z.string(),
  aggregate_kind: z.string().nullable().optional(),
  debounce_seconds: z.number().int().min(0).optional(),
  max_fires_per_hour: z.number().int().min(1).optional(),
});
export type SaveTriggerBody = z.infer<typeof saveTriggerBody>;
const saveTriggerOp: Operation<
  { slug: string },
  void,
  SaveTriggerBody,
  { ok: true; created: boolean } | { ok: false; error: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/trigger`,
  params: detailParams,
  body: saveTriggerBody,
  response: z.custom<{ ok: true; created: boolean } | { ok: false; error: string }>(),
  // `saveTriggerAction` → `/agents/workflows/<slug>` only. `FleetRow.trigger_enabled`
  // is `MAX(es.enabled)` over the loop's subscriptions, and this upsert writes the
  // trigger's SHAPE (event prefix, debounce, rate cap) without touching `enabled`
  // — so the fleet badge cannot change here. It changes on `setTriggerEnabled`
  // below, which is the one that names the fleet list.
  invalidates: [detailTarget],
};

const enabledBody = z.object({ enabled: z.boolean() });
const setTriggerEnabledOp: Operation<
  { slug: string },
  void,
  z.infer<typeof enabledBody>,
  { ok: boolean; error?: string }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/trigger/enabled`,
  params: detailParams,
  body: enabledBody,
  response: z.custom<{ ok: boolean; error?: string }>(),
  // Flips `event_subscriptions.enabled`, which is precisely what
  // `FleetRow.trigger_enabled` aggregates — the lightning badge on the fleet
  // table.
  invalidates: [detailTarget, workflowFleet.list],
};

const setTriggerAutorunOp: Operation<
  { slug: string },
  void,
  z.infer<typeof enabledBody>,
  { ok: boolean }
> = {
  method: "POST",
  path: ({ slug }) => `/api/workflows/${encodeURIComponent(slug)}/trigger/autorun`,
  params: detailParams,
  body: enabledBody,
  response: z.custom<{ ok: boolean }>(),
  // Autorun is a WORKSPACE setting (`agents.triggers.autorun`), not a per-loop
  // row, so strictly it stales every loop's detail — but the Next original
  // revalidated only `/agents/workflows/<slug>`, and this is a port of that
  // mapping, not a redesign of it. Kept slug-scoped deliberately; widening it
  // would need a workspace-settings read to exist as an Operation first, which
  // is L3 page-port work (see the RO-5-02 deferral notes).
  invalidates: [detailTarget],
};

export const workflowDetail = {
  get: detailOp,
  setStatus: setStatusOp,
  run: runOp,
  resume: resumeOp,
  optimizeNow: optimizeNowOp,
  routeStep: routeStepOp,
  setStepRole: setStepRoleOp,
  setObjective: setObjectiveOp,
  approveObjective: approveObjectiveOp,
  discardObjective: discardObjectiveOp,
  promoteVariant: promoteVariantOp,
  rollback: rollbackOp,
  approvePromotion: approvePromotionOp,
  rejectPromotion: rejectPromotionOp,
  saveTrigger: saveTriggerOp,
  setTriggerEnabled: setTriggerEnabledOp,
  setTriggerAutorun: setTriggerAutorunOp,
};
